import { withComment } from '@entities/Sst/comments/cache';
import { SstRequest } from '@entities/Sst/schema';
import { SstCommentFront, SstFront } from '@entities/Sst/type';
import { ENTITIES } from '@entities/enum';
import { FilterGroup } from '@features/ui-filters/components/types';
import { sstsApi } from '@services/api/endpoints/ssts';
import { ACTIONS, SortOrder } from '@root/src/types/common';
import { mutationOptions, QueryClient, QueryKey, queryOptions } from '@tanstack/react-query';

/**
 * SSTs (and their comments) in the React Query cache: under which key each SST read lives,
 * and which reads each SST write leaves stale. Components and hooks read and write SSTs
 * through this module only; no SST key is spelled anywhere else.
 */

/** A page of `GET /ssts`, exactly as it is requested: the key holds what the fetch sends. */
export type SstListParams = {
  page: number;
  filters: FilterGroup;
  sort: { field: string; order: SortOrder };
};

export type SstVisibilityChange = { feedbackRequestedFrom: string[]; feedbackRequested?: boolean; critical?: boolean };

/**
 * Every key starts with `sstKeys.all`, then names the resource. A write invalidates a
 * resource scope (`detail(id)`, `lists()`), which prefix-matches every read of that resource
 * and nothing else: `lists()` is every list page (SST forum, profile, a space object's
 * blocks), `detail(id)` is one SST with its comments.
 */
export const sstKeys = {
  all: ['ssts'] as const,
  lists: () => [...sstKeys.all, 'list'] as const,
  list: (params: SstListParams) => [...sstKeys.lists(), params] as const,
  detail: (sstId: string) => [...sstKeys.all, 'detail', sstId] as const,
};

const THIRTY_SECONDS = 30 * 1000;

/**
 * Every SST read. What belongs to the read itself lives here: key, fetch, stale time, and
 * `enabled` only as "no id, no fetch". A caller that adds its own `enabled` flag, polling,
 * retry or stale time spreads these options and keeps that guard.
 */
export const sstQueries = {
  /** Every list page is read with a 30 s stale time (SST forum, profile, space object blocks). */
  list: (params: SstListParams) =>
    queryOptions({
      queryKey: sstKeys.list(params),
      queryFn: () => sstsApi.getSsts(params),
      staleTime: THIRTY_SECONDS,
    }),
  /**
   * One SST with its comments. The stale time is the caller's: the SST page keeps it live
   * (websocket or polling), the blocks quoted in forum messages and the admin events cache it.
   */
  detail: (sstId: string) =>
    queryOptions({
      queryKey: sstKeys.detail(sstId),
      queryFn: () => sstsApi.getSst(sstId),
      enabled: !!sstId,
    }),
};

/**
 * Applies {@link withComment} to the cached SST, if it is cached at all. The poster's own
 * mutation and the `sst:comment:new` websocket event both come through here.
 */
export const addCommentToCachedSst = (queryClient: QueryClient, sstId: string, comment: SstCommentFront): void => {
  queryClient.setQueryData<SstFront>(sstKeys.detail(sstId), sst => (sst ? withComment(sst, comment) : sst));
};

const invalidate = async (queryClient: QueryClient, keys: QueryKey[]): Promise<void> => {
  await Promise.all(keys.map(queryKey => queryClient.invalidateQueries({ queryKey })));
};

/**
 * What each SST write leaves stale: the one place an SST write's refresh targets are
 * declared. The mutations below call them.
 *
 * Refreshes of other entities stay with the caller, through that entity's module: publishing
 * an SST with a status event also refreshes its space object through
 * `spaceObjectInvalidations.sstPublished` (`SstFormContext`), and publishing it to a forum
 * goes through `forumMutations.publishSstBlock`.
 */
export const sstInvalidations = {
  /** A new SST shows in the lists. */
  created: (queryClient: QueryClient) => invalidate(queryClient, [sstKeys.lists()]),
  /** The list rows show the settings too: private badge, critical and feedback tags (`SstBlock`). */
  updated: (queryClient: QueryClient, sstId: string) =>
    invalidate(queryClient, [sstKeys.detail(sstId), sstKeys.lists()]),
  /** The list rows tag an SST that has comments (`SstBlock`). */
  commented: (queryClient: QueryClient, sstId: string) =>
    invalidate(queryClient, [sstKeys.detail(sstId), sstKeys.lists()]),
  /** The deleted SST is dropped, not refetched: a refetch would only get a 404. */
  deleted: (queryClient: QueryClient, sstId: string) => {
    queryClient.removeQueries({ queryKey: sstKeys.detail(sstId) });
    return invalidate(queryClient, [sstKeys.lists()]);
  },
};

/**
 * Every SST write. Each refreshes through `sstInvalidations`, on the client that ran it
 * (`useMutation`'s own client, handed to the callbacks), so `useMutation(sstMutations.x())`
 * needs no `useQueryClient`.
 *
 * The refresh is requested, not awaited: none of these writes' callers (closing the modal,
 * clearing the comment box, redirecting) ever waited for the refetch, and awaiting it would
 * keep the mutation pending until the lists came back.
 */
export const sstMutations = {
  create: () =>
    mutationOptions<SstFront, Error, SstRequest>({
      mutationFn: request => sstsApi.createSst(request),
      meta: { entity: ENTITIES.SST, action: ACTIONS.CREATE },
      onSuccess: (_, __, ___, { client }) => {
        void sstInvalidations.created(client);
      },
    }),
  /** The modal shows its own toasts. */
  updateVisibility: (sstId: string) =>
    mutationOptions<SstFront, Error, SstVisibilityChange>({
      mutationFn: change => sstsApi.updateSstVisibility(sstId, change),
      meta: { skipSuccessToast: true },
      onSuccess: (_, __, ___, { client }) => {
        void sstInvalidations.updated(client, sstId);
      },
    }),
  /**
   * The websocket event and this response both add the comment to the cached SST, whichever
   * lands first (`addCommentToCachedSst` keeps one copy). The refetch that confirms it waits
   * 100 ms, as it always has.
   */
  createComment: (sstId: string) =>
    mutationOptions<SstCommentFront, Error, string>({
      mutationFn: comment => sstsApi.createSstComment(sstId, comment),
      meta: { skipSuccessToast: true },
      onSuccess: (comment, _, __, { client }) => {
        addCommentToCachedSst(client, sstId, comment);
        setTimeout(() => void sstInvalidations.commented(client, sstId), 100);
      },
    }),
  /** The deleted SST leaves the cache before the request goes out, so its page stops asking for it. */
  delete: () =>
    mutationOptions<void, Error, string>({
      mutationFn: sstId => sstsApi.deleteSst(sstId),
      meta: { entity: ENTITIES.SST, action: ACTIONS.DELETE },
      onMutate: (sstId, { client }) => {
        void sstInvalidations.deleted(client, sstId);
      },
    }),
};
